import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Button, Icon } from '@tviklax/ui-components';

export interface Vessel {
  id: string;
  name: string;
  eta: string;
  capacity: string;
  containerCount: number;
  availableContainers: number;
  nextDeparture: string;
  teuCapacity?: number;
}

export interface AvailableVesselsProps {
  carrierName: string;
  vessels: Vessel[];
  selectedVesselId?: string;
  canSelect?: boolean;
  onSelect?: (vessel: Vessel | null) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const HeaderTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const HeaderSubtext = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const VesselCard = styled.div<{ $selected: boolean }>`
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.gray200)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primary50 : theme.colors.surface)};
  box-shadow: ${({ $selected, theme }) => ($selected ? theme.shadows.md : 'none')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${({ $selected, theme }) => (!$selected ? theme.colors.gray300 : theme.colors.primary)};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const VesselName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray700};
`;

const Label = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray600};
`;

const Value = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
`;

const CapacityBadge = styled.div<{ $available: boolean }>`
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 12px;
  font-weight: 600;
  background: ${({ $available, theme }) => ($available ? theme.colors.successLight : theme.colors.warningLight)};
  color: ${({ $available, theme }) => ($available ? theme.colors.successDark : theme.colors.warningDark)};
  display: inline-flex;
  gap: 4px;
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;


export const AvailableVessels: React.FC<AvailableVesselsProps> = ({
  carrierName,
  vessels,
  selectedVesselId,
  canSelect = true,
  onSelect,
}) => {
  const theme = useTheme();
  return (
    <Container>
      <Header>
        <HeaderTitle>Available Vessels · {carrierName}</HeaderTitle>
        <HeaderSubtext>Select a vessel to finalize your booking</HeaderSubtext>
      </Header>

      {vessels.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: theme.colors.gray500, fontSize: '14px' }}>
          No vessels available for this carrier. Please select a different option.
        </div>
      ) : (
        <Grid>
          {vessels.map((vessel) => {
            const isAvailable = vessel.availableContainers > 0;
            const isSelected = selectedVesselId === vessel.id;
            return (
              <VesselCard key={vessel.id} $selected={isSelected} onClick={() => canSelect && isAvailable && onSelect?.(isSelected ? null : vessel)}>
                <VesselName>{vessel.name}</VesselName>

                <Row>
                  <Label>Next Departure</Label>
                  <Value>{vessel.nextDeparture}</Value>
                </Row>

                <Row>
                  <Label>Estimated Arrival</Label>
                  <Value>{vessel.eta}</Value>
                </Row>

                <Row>
                  <Label>Total Capacity</Label>
                  <Value>
                    {vessel.containerCount > 0
                      ? `${vessel.containerCount.toLocaleString()} TEU`
                      : vessel.capacity !== 'N/A' ? vessel.capacity : 'N/A'}
                  </Value>
                </Row>

                <CapacityBadge $available={isAvailable}>
                  {vessel.containerCount > 0 ? (
                    <>
                      <Icon name={isAvailable ? 'CheckCircle' : 'AlertTriangle'} size={12} />
                      {vessel.availableContainers} of {vessel.containerCount} available
                    </>
                  ) : (
                    'Availability not confirmed'
                  )}
                </CapacityBadge>

                <ButtonGroup>
                  <Button
                    size="sm"
                    variant={isSelected ? undefined : 'ghost'}
                    disabled={!canSelect || !isAvailable}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect?.(isSelected ? null : vessel);
                    }}
                  >
                    {isSelected ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="CheckCircle" size={13} /> Selected
                      </span>
                    ) : isAvailable ? (
                      'Select Vessel'
                    ) : (
                      'Unavailable'
                    )}
                  </Button>
                </ButtonGroup>
              </VesselCard>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};
